import React from 'react'
import { Baby, BookOpen, GraduationCap, Users, Clock, Star, Award, Heart } from 'lucide-react'
import Card from '../ui/Card'
import Button from '../ui/Button'

const ProgramsSection = () => {
const programs = [
  {
    icon: Baby,
    title: "Transición",
    subtitle: "Preescolar (5 años)",
    description: "Preparamos a los más pequeños para su primera experiencia educativa formal con metodologías lúdicas y pedagógicas especializadas.",
    features: [
      "Desarrollo psicomotor",
      "Iniciación a la lectoescritura",
      "Socialización y valores",
      "Actividades lúdicas"
    ],
    ages: "5 años",
    schedule: "Jornada Matinal y Vespertina",
    color: "from-pink-500 to-purple-500",
    bgColor: "bg-pink-50"
  },
  {
    icon: BookOpen,
    title: "Básica Primaria",
    subtitle: "Grados 1° a 5°",
    description: "Fortalecemos las competencias básicas en matemáticas, lenguaje, ciencias y sociales, desarrollando el pensamiento crítico y creativo.",
    features: [
      "Matemáticas y lenguaje",
      "Ciencias naturales",
      "Ciencias sociales",
      "Educación artística"
    ],
    ages: "6 a 10 años",
    schedule: "Vespertina (1° a 4°) - Matinal (5°)",
    color: "from-gardenia-green to-green-600",
    bgColor: "bg-green-50"
  },
  {
    icon: GraduationCap,
    title: "Básica Secundaria",
    subtitle: "Grados 6° a 9°",
    description: "Profundizamos en áreas especializadas preparando a nuestros estudiantes para la educación media y su proyecto de vida.",
    features: [
      "Materias especializadas",
      "Orientación vocacional",
      "Proyectos de investigación",
      "Formación en valores"
    ],
    ages: "11 a 15 años",
    schedule: "Jornada Matinal",
    color: "from-gardenia-red to-red-600",
    bgColor: "bg-red-50"
  },
  {
    icon: Award,
    title: "Media Académica",
    subtitle: "Grados 10° y 11°",
    description: "Preparamos a nuestros estudiantes para la educación superior y el mundo laboral con énfasis en competencias académicas avanzadas.",
    features: [
      "Preparación pruebas Saber",
      "Orientación profesional",
      "Investigación avanzada",
      "Liderazgo y emprendimiento"
    ],
    ages: "16 a 17 años",
    schedule: "Jornada Única",
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50"
  }
]

const highlights = [
  {
    icon: Users,
    title: "Atención Personalizada",
    description: "Grupos reducidos para mejor acompañamiento"
  },
  {
    icon: Star,
    title: "Excelencia Académica",
    description: "Alto nivel educativo con metodologías innovadoras"
  },
  {
    icon: Heart,
    title: "Formación Integral",
    description: "Desarrollo académico, social y emocional"
  }
]

  return (
    <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gardenia-dark mb-6">
            Nuestros 
            <span className="text-gradient block mt-2">Programas Educativos</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ofrecemos una educación integral desde transición hasta noveno grado, 
            adaptándonos a cada etapa del desarrollo de nuestros estudiantes.
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {programs.map((program, index) => {
            const IconComponent = program.icon
            return (
              <Card 
                key={index} 
                className={`${program.bgColor} border-2 border-transparent hover:border-gardenia-red/20 transition-all duration-300`}
                padding="lg"
              >
                {/* Header */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${program.color} shadow-xl mb-4`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <Card.Title className="text-2xl">{program.title}</Card.Title>
                  <Card.Subtitle className="text-gardenia-green">{program.subtitle}</Card.Subtitle>
                </div>

                {/* Description */}
                <Card.Content className="mb-6">
                  {program.description}
                </Card.Content>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white/50 rounded-lg">
                  <div className="text-center">
                    <Users className="h-5 w-5 text-gardenia-red mx-auto mb-2" />
                    <div className="text-sm font-semibold text-gardenia-dark">Edades</div>
                    <div className="text-xs text-gray-600">{program.ages}</div>
                  </div>
                  <div className="text-center">
                    <Clock className="h-5 w-5 text-gardenia-green mx-auto mb-2" />
                    <div className="text-sm font-semibold text-gardenia-dark">Horario</div>
                    <div className="text-xs text-gray-600">{program.schedule}</div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gardenia-dark mb-3">Características:</h4>
                  <ul className="space-y-2">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-gardenia-red rounded-full flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <Button 
                  to="/programas" 
                  variant="outline" 
                  className="w-full"
                >
                  Conoce Más
                </Button>
              </Card>
            )
          })}
        </div>

        {/* Highlights Section */}
        <div className="bg-gradient-to-r from-gardenia-red/5 to-gardenia-green/5 rounded-2xl p-8 md:p-12 mb-16">
          <h3 className="text-2xl md:text-3xl font-bold text-gardenia-dark text-center mb-12">
            ¿Por qué elegir Las Gardenias?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => {
              const IconComponent = highlight.icon
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg mb-4">
                    <IconComponent className="h-8 w-8 text-gardenia-red" />
                  </div>
                  <h4 className="text-lg font-semibold text-gardenia-dark mb-2">
                    {highlight.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Admission CTA */}
        <div className="text-center bg-gradient-to-r from-gardenia-red to-gardenia-green rounded-2xl p-8 md:p-12 text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            ¡Admisiones Abiertas 2026!
          </h3>
          <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Inscribe a tu hijo en una de las mejores instituciones educativas de Barranquilla. 
            El proceso de admisión ya está abierto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              to="/admisiones" 
              variant="gold"
              size="lg"
            >
              Proceso de Admisión
            </Button>
            <Button 
              to="/contacto" 
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-gardenia-red"
            >
              Solicitar Información
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProgramsSection